import * as React from "react";
import { IconChevronLeft } from "../../components/icons.jsx";
import { eventRequest, purchaseRequest, chestRequest, pendingChestRequest } from "./event-api.js";
import { EventOffer, EventOfferSheet } from "./EventOffer.jsx";
import { EventActivities } from "./EventActivities.jsx";
import { EventToast } from "./EventToast.jsx";
import { RewardReadyBadge } from "./RewardReadyBadge.jsx";
import { EventCalendarPage } from "./EventCalendar.jsx";
import { EventShopPage } from "./EventShop.jsx";
import { EventChestPage } from "./EventChest.jsx";
import { OpeningReveal } from "../opening/OpeningReveal.jsx";
import { chestRevealResult } from "./chest-reveal.js";
import { IconCalendar } from "@tabler/icons-react";
import "./event-hub.css";
import "./event-menu.css";
import "./event-shortcuts.css";

const number = value => Number(value).toLocaleString("ru-RU");
const date = value => new Date(`${value}T12:00:00+03:00`).toLocaleDateString("ru-RU", { day: "numeric", month: "long", timeZone: "Europe/Moscow" });

export function HalloweenEventPage({ onBack, onOpenFlight, onRewardsChanged }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const dismissNotice = React.useCallback(() => setNotice(""), []);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [selectedKey, setSelectedKey] = React.useState(null);
  const [chestPending, setChestPending] = React.useState(null);
  const [chestResult, setChestResult] = React.useState(null);
  const calendarOpen = selectedKey === "calendar";
  const shopOpen = selectedKey === "shop";
  const chestOpen = selectedKey === "chest";
  React.useEffect(() => { window.scrollTo(0, 0); }, [calendarOpen, shopOpen, chestOpen]);
  const lock = React.useRef(false);
  const backButton = window.Telegram?.WebApp?.BackButton;
  React.useEffect(() => {
    if (!backButton) return;
    const goBack = () => {
      if (chestResult) { setChestResult(null); return; }
      const hint = document.querySelector(".event-chest-hint:popover-open");
      if (hint) { hint.hidePopover(); return; }
      const confirmation = document.querySelector(".event-chest-dialog[open]");
      if (confirmation) { confirmation.dispatchEvent(new Event("cancel", { cancelable: true })); return; }
      selectedKey ? setSelectedKey(null) : onBack();
    };
    backButton.show(); backButton.onClick(goBack);
    return () => { backButton.offClick(goBack); backButton.hide(); };
  }, [backButton, onBack, selectedKey, chestResult]);
  const load = React.useCallback(async signal => {
    setLoading(true); setError("");
    try {
      const result = await eventRequest("state", {}, signal);
      if (!signal?.aborted) {
        setData(result);
        setChestPending(result.event ? pendingChestRequest(result.event.id) : null);
      }
    }
    catch (failure) { if (!signal?.aborted) setError(failure.message || "Не удалось загрузить событие"); }
    finally { if (!signal?.aborted) setLoading(false); }
  }, []);
  React.useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const receive = async (offer, mode) => {
    if (lock.current || !data?.event) return;
    lock.current = true; setBusy(`${offer.key}:${mode}`); setNotice(""); setError("");
    try {
      const pending = purchaseRequest(data.event.id, offer.key, mode);
      const { storageKey, ...body } = pending;
      await eventRequest("offer", body);
      sessionStorage.removeItem(storageKey);
      setNotice("Награды получены!");
      await load();
      try { await onRewardsChanged?.(); }
      catch { setNotice("Награды получены. Баланс обновится после перезагрузки приложения."); }
    } catch (failure) {
      setError(`${failure.message || "Связь прервалась"} Повторное нажатие безопасно.`);
    } finally { lock.current = false; setBusy(""); }
  };
  const receiveCalendar = async day => {
    if (lock.current || loading || !data?.event) return;
    lock.current = true; setBusy("calendar"); setNotice(""); setError("");
    try {
      await eventRequest("calendar", { eventId: data.event.id, day, mode: data.event.calendarMode || "date" });
      if (data.event.calendarMode !== "claim_sequence") setData(previous => ({ ...previous, event: { ...previous.event, calendar: previous.event.calendar.map(item => item.day === day ? { ...item, claimed: true, canClaim: false } : item) } }));
      setNotice("Награда получена!");
      await load();
      try { await onRewardsChanged?.(); }
      catch { setNotice("Награда получена. Баланс обновится после перезагрузки приложения."); }
    } catch (failure) {
      setError(`${failure.message || "Связь прервалась"} Повторное нажатие безопасно.`);
    } finally { lock.current = false; setBusy(""); }
  };
  const buyShop = async item => {
    if (lock.current || loading || !data?.event) return;
    lock.current = true; setBusy(`shop:${item.id}`); setNotice(""); setError("");
    try {
      const pending = purchaseRequest(data.event.id, item.id, "shop");
      await eventRequest("shop", { eventId: data.event.id, itemId: item.id, requestId: pending.requestId });
      sessionStorage.removeItem(pending.storageKey);
      setNotice("Покупка получена!");
      await load();
      try { await onRewardsChanged?.(); }
      catch { setNotice("Покупка получена. Баланс обновится после перезагрузки приложения."); }
    } catch (failure) {
      setError(`${failure.message || "Связь прервалась"} Повторное нажатие безопасно.`);
    } finally { lock.current = false; setBusy(""); }
  };
  const changeChest = async action => {
    if (lock.current || loading || !data?.event?.chest) return;
    lock.current = true; setError(""); setNotice("");
    let request;
    try {
      request = chestRequest(data.event.id, action, data.event.chest.level);
      setChestPending(request); setBusy(`chest:${request.action}`);
      const { storageKey, ...body } = request;
      const result = await eventRequest("chest", body);
      sessionStorage.removeItem(storageKey); setChestPending(null);
      if (request.action === "open" && result.rewards?.length) setChestResult(chestRevealResult(result.rewards));
      else setNotice(request.action === "open" ? result.message || "Награды зачислены!" : "Рундук улучшен!");
      await load();
      try { await onRewardsChanged?.(); }
      catch { setNotice("Готово. Баланс аккаунта обновится после перезагрузки приложения."); }
    } catch (failure) {
      if (failure.status === 400 && request) {
        sessionStorage.removeItem(request.storageKey); setChestPending(null);
        await load();
      }
      setError(failure.message || "Связь прервалась. Повторите незавершённый запрос.");
    } finally { lock.current = false; setBusy(""); }
  };
  React.useEffect(() => {
    const refresh = () => { if (!document.hidden && !lock.current) load(); };
    if (calendarOpen) refresh();
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [calendarOpen, load]);
  const event = data?.event;
  const calendarReady = event?.active && event.calendar?.some(day => day.canClaim && !day.claimed);
  const selectedOffer = data?.offers?.find(offer => offer.key === selectedKey);
  const labels = { currency: event?.currencyName, spins: "Крутки", crystals: "Кристаллы", gold: "MetaCoin", cases: "Кейсы ивента", default_cases: "Обычные кейсы" };
  if (calendarOpen) return <EventCalendarPage event={event} onBack={() => setSelectedKey(null)} showBack={!backButton} onClaim={receiveCalendar} {...{ busy, loading, error, notice }} onDismissNotice={dismissNotice} onReload={() => load()} />;
  if (shopOpen && event) return <EventShopPage event={event} items={data.shop || []} onBack={() => setSelectedKey(null)} showBack={!backButton}
    {...{ busy, loading, error, notice }} onDismissNotice={dismissNotice} onBuy={buyShop} onReload={() => load()} />;
  if (chestOpen && event?.chest?.enabled) return <><EventChestPage event={event} onBack={() => setSelectedKey(null)} showBack={!backButton}
    {...{ busy, loading, error, notice }} pending={chestPending} onDismissNotice={dismissNotice} onChange={changeChest} onReload={() => load()} />
    {chestResult && <OpeningReveal result={chestResult} onClose={() => setChestResult(null)} />}</>;
  return <section className="halloween-event-page halloween-hub" aria-label="Хэллоуин" aria-busy={loading}>
    <div className="halloween-menu-banner">
      <img className="halloween-menu-scenery" src="/assets/night-flight-illustrated/castle-panorama.webp" alt="" />
      <img className="halloween-menu-witch" src="/assets/night-flight-illustrated/witch.webp" alt="" />
      <img className="halloween-menu-pumpkin" src="/assets/home-halloween/pumpkin-v2.webp" alt="" />
      <div className="halloween-menu-banner-top">
        {!backButton && <button type="button" className="halloween-event-back" onClick={onBack} aria-label="Назад на главную"><IconChevronLeft /></button>}
        {event && <span className="halloween-menu-deadline">до {date(event.end)}</span>}
      </div>
      <div className="halloween-banner-footer">
        <div className="halloween-menu-title"><small>СЕЗОННОЕ СОБЫТИЕ</small><h2>{event?.title || "Хэллоуин"}</h2></div>
        <p className="halloween-banner-caption">Маленькие подарки.<br />Большие приключения.</p>
        <div className="halloween-menu-balance" aria-label={event ? `${event.currencyName}: ${number(event.currency)}` : "Баланс события загружается"}>
          <img src="/assets/home-halloween/pumpkin-v2.webp" alt="" />
          <div><b>{event ? number(event.currency) : "—"}</b><span>{event?.currencyName || "Валюта события"}</span></div>
        </div>
      </div>
    </div>
    <nav className="event-shortcuts" aria-label="Разделы события">
      <button type="button" className="event-shortcut" onClick={() => setSelectedKey("calendar")} aria-label="Календарь наград">
        <span className="event-shortcut-art event-shortcut-calendar"><IconCalendar aria-hidden="true" />{calendarReady && <RewardReadyBadge label="Можно забрать подарок" />}</span><b>Календарь</b>
      </button>
      <button type="button" className="event-shortcut" onClick={() => setSelectedKey("shop")} disabled={!data?.shop?.length} aria-label="Магазин события">
        <span className="event-shortcut-art"><img src="/assets/home-halloween/pumpkin-v2.webp" alt="" /></span><b>Лавка</b>
      </button>
      {["stock", "set"].map(key => {
        const offer = data?.offers?.find(value => value.key === key);
        const ready = event?.active && offer?.enabled && offer.canClaim && !offer.claimed;
        return <button type="button" className="event-shortcut" key={key} disabled={!offer} onClick={() => setSelectedKey(key)} aria-haspopup="dialog" aria-label={key === "stock" ? "Акции события" : "Набор события"}>
          <span className="event-shortcut-art"><img src={key === "stock" ? "/assets/resource-emojis/card.webp" : "/assets/case-halloween.webp"} alt="" />{ready && <RewardReadyBadge />}</span><b>{key === "stock" ? "Акции" : "Набор"}</b>
        </button>;
      })}
    </nav>
    {error && <div className="halloween-event-message" role="alert"><p>{error}</p><button type="button" disabled={!!busy || loading} onClick={() => load()}>Обновить</button></div>}
    {!selectedOffer && selectedKey !== "shop" && <EventToast message={notice} onDismiss={dismissNotice} />}
    {loading && <p role="status">Загружаем событие…</p>}
    {!loading && data && !data.available && <p className="halloween-event-message">Предложения Хэллоуина сейчас недоступны. В «Ночном полёте» остаются тренировка и архив.</p>}
    {event && !event.active && <p className="halloween-event-message">Событие сейчас не активно. Получение предложений недоступно.</p>}
    <EventActivities activities={[...(event?.chest?.enabled ? [{
      key: "chest", title: "Рундук", image: "/assets/event-chest/chest.webp",
      description: `Уровень ${event.chest.level} из ${event.chest.maxLevel}`,
      action: "К рундуку", onOpen: () => setSelectedKey("chest"),
    }] : []), {
      key: "night-flight", title: "Ночной полёт", image: "/assets/night-flight-illustrated/witch.webp",
      description: event?.active ? "Собирай тыквы и побей рекорды" : "Тренировка и архив турниров",
      action: "В полёт", onOpen: onOpenFlight,
    }]} />
    {selectedOffer && <EventOfferSheet onClose={() => setSelectedKey(null)}>
      {error && <p className="halloween-event-message" role="alert">{error}</p>}
      <EventToast message={notice} onDismiss={dismissNotice} />
      <EventOffer offer={selectedOffer} {...{ labels, busy, loading, receive }} />
    </EventOfferSheet>}
  </section>;
}
